import React from "react"
import { useTranslation } from "react-i18next"

import {
  ComplianceDates,
  ComplianceEmissionFactorName,
  ComplianceEmissionFactorValue,
  ComplianceFines,
  ComplianceReduction,
  ComplianceStandards,
  ComplianceStatus,
  ComplianceStatusReduced,
  ComplianceTargetUsageIntensity,
  ComplianceTotalEmissions,
  ComplianceUsageIntensity,
} from "@/components/compliance"
import { useUnit } from "@/components/unit/use-unit"
import { usePercentSymbol } from "@/hooks/use-percent-symbol"
import { useCurrencySymbol } from "@/hooks/useCurrencySymbol/useCurrencySymbol"
import type { Department } from "@/models/department"
import { UnitName } from "@/models/unit"
import {
  DataGridColumnHeader,
  baseColumnDef,
  baseNumberColumnDef,
} from "@/nzds/data-grid"
import { useCommonSiteDataGridColumns } from "@/utils/site/use-common-site-data-grid-columns"

import { Stack, Typography } from "@mui/material"
import type {
  GridColDef,
  GridColumnVisibilityModel,
  GridPinnedColumnFields,
  GridRenderCellParams,
  GridRowId,
  GridRowModesModel,
  GridSortModel,
} from "@mui/x-data-grid-pro"

import type { SiteExplorerGridRecord } from "../../../../services/use-get-site-explorer"
import { useCommonSiteExplorerGridColumns } from "../../../utils/use-common-site-explorer-grid-columns/use-common-site-explorer-grid-columns"
import { useCommonSiteGridColumns } from "../../../utils/use-common-site-grid-columns"

export const defaultColumnVisibilityModel: GridColumnVisibilityModel = {
  electricity_ef: false, // Electricity EF Value
  electricity_source: false, // Electricity EF
  natural_gas_ef: false, // Natural Gas EF Value
  natural_gas_source: false, // Natural Gas EF
  sq_ft: false, // Building Area
  total_emissions: false, // Combined Emissions
}

export const defaultPinnedColumns: GridPinnedColumnFields = {
  left: ["rowNumber", "site_name"],
}

export const defaultSortModel: GridSortModel = [
  {
    field: "site_name",
    sort: "desc",
  },
]

interface UseComplianceGridColumnsProps {
  buildingTypes: string[] | undefined
  departments: Department[] | undefined
  handleRowEditCancelClick: (id: GridRowId) => void
  handleRowEditClick: (id: GridRowId) => void
  handleRowEditSaveClick: (id: GridRowId) => void
  rowModesModel: GridRowModesModel
}

export const useComplianceGridColumns = (
  props: UseComplianceGridColumnsProps
): GridColDef<SiteExplorerGridRecord>[] => {
  const { t } = useTranslation()
  const currencySymbol = useCurrencySymbol()
  const percentSymbol = usePercentSymbol()

  const kbtuPerSqFtUnit = useUnit(
    UnitName.KiloBritishThermalUnitPerSquareFoot,
    null
  )
  const kgCo2ePerSqFtUnit = useUnit(
    UnitName.KilogramsOfCarbonDioxideEquivalentPerSquareFoot,
    null
  )
  const kgCo2ePerKbtuUnit = useUnit(
    UnitName.KilogramsOfCarbonDioxideEquivalentPerKiloBritishThermalUnit,
    null
  )
  const kgCo2ePerKwhUnit = useUnit(
    UnitName.KilogramsOfCarbonDioxideEquivalentPerKilowattHour,
    null
  )
  const mtCo2eUnit = useUnit(UnitName.MetricTonsOfCarbonDioxideEquivalent)

  const commonSiteGridColumns =
    useCommonSiteGridColumns<SiteExplorerGridRecord>()
  const commonSiteExplorerGridColumns = useCommonSiteExplorerGridColumns(props)
  const commonSiteDataGridColumns =
    useCommonSiteDataGridColumns<SiteExplorerGridRecord>()

  const columns: GridColDef<SiteExplorerGridRecord>[] = React.useMemo(() => {
    return [
      {
        ...commonSiteGridColumns.rowNumber,
      },
      {
        ...commonSiteGridColumns.siteName,
      },
      {
        ...baseColumnDef,
        display: "flex",
        field: "in_compliance",
        headerName: t("gridColumn.complianceStatus.name", { ns: "common" }),
        renderCell: (params: GridRenderCellParams<SiteExplorerGridRecord>) => (
          <ComplianceStatusReduced regulations={params.row.regulations} />
        ),
        renderHeader: () => (
          <DataGridColumnHeader
            field="in_compliance"
            headerName={t("gridColumn.complianceStatus.name", { ns: "common" })}
          />
        ),
        sortable: false,
        width: 160,
      },
      {
        ...baseColumnDef,
        display: "flex",
        field: "regulation_name",
        headerName: t("gridColumn.complianceStandard.name", { ns: "common" }),
        renderCell: (params: GridRenderCellParams<SiteExplorerGridRecord>) => (
          <ComplianceStandards regulations={params.row.regulations} />
        ),
        renderHeader: () => (
          <DataGridColumnHeader
            field="regulation_name"
            headerName={t("gridColumn.complianceStandard.name", {
              ns: "common",
            })}
            tooltipContent={
              <>
                <Typography
                  variant="body2"
                  sx={{
                    mb: 1.5,
                  }}
                >
                  {t("gridColumn.complianceStandard.description", {
                    ns: "common",
                  })}
                </Typography>
                <Stack
                  spacing={0.5}
                  sx={{
                    mb: 1,
                    ml: 0.5,
                  }}
                >
                  <ComplianceStatus isCompliant={true} />
                  <ComplianceStatus isCompliant={false} />
                </Stack>
              </>
            }
          />
        ),
        sortable: false,
      },
      {
        ...baseColumnDef,
        display: "flex",
        field: "effective_date",
        headerName: t("gridColumn.compliancePeriod.name", { ns: "common" }),
        renderCell: (params: GridRenderCellParams<SiteExplorerGridRecord>) => (
          <ComplianceDates regulations={params.row.regulations} />
        ),
        renderHeader: () => (
          <DataGridColumnHeader
            field="effective_date"
            headerName={t("gridColumn.compliancePeriod.name", { ns: "common" })}
          />
        ),
        sortable: false,
        width: 160,
      },
      {
        ...baseNumberColumnDef,
        field: "compliance_penalties",
        headerName: t("gridColumn.complianceFines.name", { ns: "common" }),
        renderCell: (params: GridRenderCellParams<SiteExplorerGridRecord>) => (
          <ComplianceFines regulations={params.row.regulations} />
        ),
        renderHeader: () => (
          <DataGridColumnHeader
            field="compliance_penalities"
            headerName={t("gridColumn.complianceFines.name", { ns: "common" })}
            unit={currencySymbol}
          />
        ),
        sortable: false,
        width: 140,
      },
      {
        ...baseNumberColumnDef,
        display: "flex",
        field: "reduction_required",
        headerName: t("gridColumn.complianceReduction.name", { ns: "common" }),
        renderCell: (params: GridRenderCellParams<SiteExplorerGridRecord>) => (
          <ComplianceReduction regulations={params.row.regulations} />
        ),
        renderHeader: () => (
          <DataGridColumnHeader
            field="reduction_required"
            headerName={t("gridColumn.complianceReduction.name", {
              ns: "common",
            })}
            unit={percentSymbol}
          />
        ),
        sortable: false,
        width: 180,
      },
      // Combined Emissions is unique to Compliance
      // The value is calculated based on the regulation emission factor
      {
        ...baseNumberColumnDef,
        field: "total_emissions",
        headerName: t("gridColumn.combinedEmissions.name", { ns: "common" }),
        renderCell: (params: GridRenderCellParams<SiteExplorerGridRecord>) => (
          <ComplianceTotalEmissions regulations={params.row.regulations} />
        ),
        renderHeader: () => (
          <DataGridColumnHeader
            field="total_emissions"
            headerName={t("gridColumn.combinedEmissions.name", {
              ns: "common",
            })}
            unit={mtCo2eUnit}
          />
        ),
        sortable: false,
        width: 160,
      },
      {
        ...baseNumberColumnDef,
        field: "calculated_eui",
        headerName: t("gridColumn.energyUsageIntensity.name", { ns: "common" }),
        renderCell: (params: GridRenderCellParams<SiteExplorerGridRecord>) => (
          <ComplianceUsageIntensity
            regulations={params.row.regulations}
            usageIntensityType="eui"
          />
        ),
        renderHeader: () => (
          <DataGridColumnHeader
            field="calculated_eui"
            headerName={t("gridColumn.energyUsageIntensity.name", {
              ns: "common",
            })}
            unit={kbtuPerSqFtUnit}
          />
        ),
        sortable: false,
      },
      {
        ...baseNumberColumnDef,
        field: "target_eui",
        headerName: t("gridColumn.targetEui.name", { ns: "common" }),
        renderCell: (params: GridRenderCellParams<SiteExplorerGridRecord>) => (
          <ComplianceTargetUsageIntensity
            regulations={params.row.regulations}
            usageIntensityType="eui"
          />
        ),
        renderHeader: () => (
          <DataGridColumnHeader
            field="target_eui"
            headerName={t("gridColumn.targetEui.name", { ns: "common" })}
            unit={kbtuPerSqFtUnit}
          />
        ),
        sortable: false,
        width: 120,
      },
      {
        ...baseNumberColumnDef,
        field: "calculated_cui",
        headerName: t("gridColumn.carbonUsageIntensity.name", { ns: "common" }),
        renderCell: (params: GridRenderCellParams<SiteExplorerGridRecord>) => (
          <ComplianceUsageIntensity
            regulations={params.row.regulations}
            usageIntensityType="cui"
          />
        ),
        renderHeader: () => (
          <DataGridColumnHeader
            field="calculated_cui"
            headerName={t("gridColumn.carbonUsageIntensity.name", {
              ns: "common",
            })}
            unit={kgCo2ePerSqFtUnit}
          />
        ),
        sortable: false,
      },
      {
        ...baseNumberColumnDef,
        field: "target_cui",
        headerName: t("gridColumn.targetCui.name", { ns: "common" }),
        renderCell: (params: GridRenderCellParams<SiteExplorerGridRecord>) => (
          <ComplianceTargetUsageIntensity
            regulations={params.row.regulations}
            usageIntensityType="cui"
          />
        ),
        renderHeader: () => (
          <DataGridColumnHeader
            field="target_cui"
            headerName={t("gridColumn.targetCui.name", { ns: "common" })}
            unit={kgCo2ePerSqFtUnit}
          />
        ),
        sortable: false,
        width: 120,
      },
      // Combined Energy Usage is unique to Compliance
      // The value is calculated based on the regulation emission factor
      {
        ...commonSiteDataGridColumns.combinedEnergyUsage,
        sortable: false,
      },
      {
        ...baseColumnDef,
        field: "electricity_source",
        headerName: t("gridColumn.electricityEf.name", { ns: "common" }),
        renderCell: (params: GridRenderCellParams<SiteExplorerGridRecord>) => (
          <ComplianceEmissionFactorName
            emissionFactorType="electricity"
            regulations={params.row.regulations}
          />
        ),
        renderHeader: () => (
          <DataGridColumnHeader
            field="electricity_source"
            headerName={t("gridColumn.electricityEf.name", { ns: "common" })}
          />
        ),
        sortable: false,
        width: 120,
      },
      {
        ...baseNumberColumnDef,
        display: "flex",
        field: "electricity_ef",
        headerName: t("gridColumn.electricityEfValue.name", { ns: "common" }),
        renderCell: (params: GridRenderCellParams<SiteExplorerGridRecord>) => (
          <ComplianceEmissionFactorValue
            emissionFactorType="electricity"
            regulations={params.row.regulations}
          />
        ),
        renderHeader: () => (
          <DataGridColumnHeader
            field="electricity_ef"
            headerName={t("gridColumn.electricityEfValue.name", {
              ns: "common",
            })}
            unit={kgCo2ePerKwhUnit}
          />
        ),
        sortable: false,
        width: 160,
      },
      {
        ...baseColumnDef,
        display: "flex",
        field: "natural_gas_source",
        headerName: t("gridColumn.naturalGasEf.name", { ns: "common" }),
        renderCell: (params: GridRenderCellParams<SiteExplorerGridRecord>) => (
          <ComplianceEmissionFactorName
            emissionFactorType="natural_gas"
            regulations={params.row.regulations}
          />
        ),
        renderHeader: () => (
          <DataGridColumnHeader
            field="natural_gas_source"
            headerName={t("gridColumn.naturalGasEf.name", { ns: "common" })}
          />
        ),
        sortable: false,
        width: 132,
      },
      {
        ...baseNumberColumnDef,
        field: "natural_gas_ef",
        headerName: t("gridColumn.naturalGasEfValue.name", { ns: "common" }),
        renderCell: (params: GridRenderCellParams<SiteExplorerGridRecord>) => (
          <ComplianceEmissionFactorValue
            emissionFactorType="natural_gas"
            regulations={params.row.regulations}
          />
        ),
        renderHeader: () => (
          <DataGridColumnHeader
            field="natural_gas_ef"
            headerName={t("gridColumn.naturalGasEfValue.name", {
              ns: "common",
            })}
            unit={kgCo2ePerKbtuUnit}
          />
        ),
        sortable: false,
        width: 172,
      },
      {
        ...commonSiteExplorerGridColumns.buildingArea,
      },
    ]
  }, [
    commonSiteGridColumns.rowNumber,
    commonSiteGridColumns.siteName,
    commonSiteDataGridColumns.combinedEnergyUsage,
    commonSiteExplorerGridColumns.buildingArea,
    currencySymbol,
    mtCo2eUnit,
    kbtuPerSqFtUnit,
    kgCo2ePerSqFtUnit,
    kgCo2ePerKwhUnit,
    kgCo2ePerKbtuUnit,
    percentSymbol,
    t,
  ])

  return columns
}
